export class MonthYear
{
    Year: number;
    Month: number; // 1-based
}

export class MonthYearHelpers
{
    static build(year: number, month: number): MonthYear | null
    {
        if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12)
        {
            const result = new MonthYear();
            result.Year = year;
            result.Month = month;
            return result;
        }

        return null;
    }

    static buildFromDate(date: Date): MonthYear | null
    {
        if (date instanceof Date && !isNaN(date.getTime()))
        {
            return MonthYearHelpers.build(date.getFullYear(), date.getMonth() + 1);
        }

        return null;
    }

    static buildFromString(dateStr: string | null): MonthYear | null
    {
        if (dateStr)
        {
            const date = new Date(dateStr);
            return MonthYearHelpers.buildFromDate(date);
        }

        return null;
    }

    static buildFromMonthYearString(monthYearString: string | null): MonthYear | null
    {
        if (monthYearString)
        {
            const parts = monthYearString.split('/');

            if (parts.length === 2)
            {
                const month = parseInt(parts[0], 10);
                const year = parseInt(parts[1], 10);
                if (!isNaN(month) && !isNaN(year) && month >= 1 && month <= 12)
                {
                    return MonthYearHelpers.build(year, month);
                }
            }
        }

        return null;
    }


    static copyNullable(source: MonthYear | null | undefined): MonthYear | null
    {
        if (!source)
        {
            return null;
        }

        return MonthYearHelpers.build(source.Year, source.Month);
    }

    static areEqual(first: MonthYear | null, second: MonthYear | null): boolean
    {
        if (first == null && second == null)
        {
            return true; // Both are null, so they are equal
        }

        if (!first || !second)
        {
            return false; // One is null and the other is not
        }

        return first.Year === second.Year && first.Month === second.Month;
    }


    static toDate(source: MonthYear | null): Date | null
    {
        if (!source) return null;
        return new Date(source.Year, source.Month - 1, 1);
    }

    static toMonthYearString(source: MonthYear | null): string | null
    {
        if (source?.Year && source?.Month)
        {
            return `${source.Month}/${source.Year}`;
        }

        return null;
    }

    static toMonthYearLongString(source: MonthYear | null): string | null
    {
        if (!source)
        {
            return null;             
        }

        const date = new Date(source.Year, source.Month - 1, 1);
        const formattedMonth = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(date);
        return `${formattedMonth}, ${source.Year}`;
    }

    //equals(other: MonthYear): boolean
    //{
    //    if (!other) return false;
    //    return this.Year === other.Year && this.Month === other.Month;
    //}
}

export class ValidationError
{
    Context: string;
    Error: string;
    ErrorCode: string;

    Value: string;
    Data: string[];
    DateTimeValue: string;
}

export class DependentEntityInfo
{
    Id: string;
    Name: string;
    Url?: string | null;
    Path?: string | null;
}

export enum RagStatus
{
    R = "R", // Indicates a critical or negative status
    A = "A", // Indicates a warning or intermediate status
    G = "G"  // Indicates a positive or healthy status
}


export enum ProjectLifecycle
{
    Proposal = 'Proposal',
    Qualified = 'Qualified',
    Rejected = 'Rejected',
    Scheduled = 'Scheduled',
    InProgress = 'InProgress',
    Paused = 'Paused',
    Completed = 'Completed',
    Terminated = 'Terminated'
}

//lifecycleColors: Record<ProjectLifecycle, string> = {
//    'Proposal': "#B0E0E6",         // Powder Blue - Represents proposals under review.
//    'Rejected': "#FFC0CB",         // Pink - Represents proposals that were reviewed but won't be pursued.
//    'Candidate': "#4169E1",        // Royal Blue - Represents proposals that have passed an initial assessment.
//    'Accepted': "#FFD700",         // Gold - Represents projects that have been selected but not yet scheduled.
//    'Scheduled': "#20B2AA",        // Green-Blue (Teal) - Represents projects that are set and ready to go.
//    'Started': "#32CD32",          // Bright Green - Represents projects that are actively in progress.
//    'Paused': "#FFA500",           // Orange - Represents projects that are on hold.
//    'Completed': "#008000",        // Deep Green - Represents successful completion.
//    'Discontinued': "#FF0000"      // Red - Represents projects that were stopped or terminated.
//}

export enum ProjectAcceptance
{
    TBD = 'TBD',
    Accepted = 'Accepted',
    Rejected = 'Rejected',
}

export enum TimeFrameGranularity
{
    Monthly = 'Monthly',   // Data is segmented into monthly intervals
    Quarterly = 'Quarterly', // Data is segmented into quarterly intervals
    Yearly = 'Yearly'  // Data is segmented into yearly intervals
}

export enum CurrencyDenomination
{
    Unit = 'Unit',
    Thousand = 'Thousand',
    Million = 'Million',
    Billion = 'Billion',
    Trillion = 'Trillion',
}

export enum PlanningValueType
{
    Estimated = 'Estimated',
    Committed = 'Committed',
    Planned = 'Planned',
}


